/**
 * Why Choose Us — Marquee pause/resume for touch & accessibility
 *
 * Architecture:
 *
 * The marquee animation is driven entirely by CSS @keyframes.
 * This custom element handles:
 *
 *   1. Touch-device pause — On touch devices there's no :hover,
 *      so we toggle animation-play-state on tap.
 *
 *   2. Focus management — Pauses the marquee when a focusable pill
 *      (link) inside the track receives keyboard focus, so the user
 *      can read and activate it without the content sliding away.
 *
 *   3. Reduced motion — Already handled in CSS, but we also skip
 *      binding listeners when the preference is active.
 */

if (!customElements.get('why-choose-us')) {
  customElements.define(
    'why-choose-us',
    class WhyChooseUs extends HTMLElement {
      /** @type {HTMLElement[]} */
      tracks = [];

      connectedCallback() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

        this.tracks = [...this.querySelectorAll('.why-choose__track')];

        this.setupFocusPause();
        this.setupTouchPause();
      }

      disconnectedCallback() {
        for (const track of this.tracks) {
          track.removeEventListener('focusin', track._focusIn);
          track.removeEventListener('focusout', track._focusOut);
        }
      }

      /* ── Pause when a link inside the track receives focus ──────── */

      setupFocusPause() {
        for (const track of this.tracks) {
          track._focusIn = () => {
            track.style.animationPlayState = 'paused';
          };
          track._focusOut = () => {
            track.style.animationPlayState = '';
          };
          track.addEventListener('focusin', track._focusIn);
          track.addEventListener('focusout', track._focusOut);
        }
      }

      /* ── Toggle pause on tap for touch-only devices ────────────── */

      setupTouchPause() {
        if (!window.matchMedia('(pointer: coarse)').matches) return;

        for (const track of this.tracks) {
          const row = track.closest('.why-choose__row');
          if (!row) continue;

          row.addEventListener('click', (e) => {
            /* Don't interfere with pill-link clicks */
            if (e.target.closest('a.why-choose__pill')) return;

            const isPaused = track.style.animationPlayState === 'paused';
            track.style.animationPlayState = isPaused ? '' : 'paused';
          });
        }
      }
    }
  );
}
